import { COLLATERAL_LOGOS } from "../../common/collateralTableColumns";
import { CSSProperties, FC, Fragment, useId } from "react";
import { Tooltip } from "react-tooltip";
import { Decimal, Fiat, IconDenom } from "rujira.ui";
import { PositionBorrowRowFragment$data } from "./__generated__/PositionBorrowRowFragment.graphql";

type CollateralEntry = PositionBorrowRowFragment$data["collaterals"][number];

type BalanceEntry = CollateralEntry & {
  collateral: Extract<CollateralEntry["collateral"], { __typename: "Balance" }>;
};

const balanceEntries = (collaterals: readonly CollateralEntry[]) =>
  collaterals.filter(
    (c): c is BalanceEntry => c.collateral.__typename === "Balance"
  );

export const MultiAssetLogos: FC<{
  collaterals: readonly CollateralEntry[];
}> = ({ collaterals }) => {
  const tooltipId = useId();
  const entries = balanceEntries(collaterals);
  const single = entries.length === 1;

  return (
    <div
      className="collateral-tokens"
      style={
        {
          "--collateral-logo-overlap": `${COLLATERAL_LOGOS.overlap}rem`,
        } as CSSProperties
      }>
      {entries.map((entry, idx) => {
        const { asset, amount } = entry.collateral;
        const tipId = `${tooltipId}-${idx}`;

        return (
          <Fragment key={idx}>
            <span
              data-tooltip-id={single ? undefined : tipId}
              className="collateral-tokens__icon flex"
              style={{
                width: `${single ? COLLATERAL_LOGOS.single : COLLATERAL_LOGOS.multiple}rem`,
              }}>
              <IconDenom
                denom={asset.metadata.symbol}
                className="block w-full"
              />
            </span>
            {!single && (
              <Tooltip id={tipId} className="tooltip">
                <div className="condensed">
                  <Fiat
                    className="fs-16 fw-500"
                    amount={entry.valueFull}
                    symbol="$"
                    decimals={8}
                    clip
                  />
                  <div className="fs-14 color-grey fw-500">
                    <Decimal
                      amount={amount}
                      symbol={asset.metadata.symbol}
                      subscript
                      clip
                    />
                  </div>
                </div>
              </Tooltip>
            )}
          </Fragment>
        );
      })}
    </div>
  );
};

export const MultiAssetValue: FC<{
  collaterals: readonly CollateralEntry[];
}> = ({ collaterals }) => {
  const entries = balanceEntries(collaterals);
  const totalValue = collaterals.reduce((a, v) => a + v.valueFull, 0n);

  if (entries.length !== 1) {
    return (
      <Fiat
        amount={totalValue}
        symbol="$"
        decimals={8}
        className="fs-16"
        clip
      />
    );
  }

  const { asset, amount } = entries[0].collateral;
  return (
    <div className="flex dir-c">
      <Fiat
        amount={entries[0].valueFull}
        symbol="$"
        decimals={8}
        className="fs-16"
        clip
      />
      <Decimal
        className="color-grey fs-14 condensed fw-500"
        amount={amount}
        symbol={asset.metadata.symbol}
        subscript
        clip
      />
    </div>
  );
};

// Compose the same logo and value renderers for combined/mobile cells.
export const MultiAsset: FC<{ collaterals: readonly CollateralEntry[] }> = ({
  collaterals,
}) => (
  <div
    className="flex ai-c"
    style={{
      gap: balanceEntries(collaterals).length === 1 ? "0.875rem" : "0.75rem",
    }}>
    <MultiAssetLogos collaterals={collaterals} />
    <MultiAssetValue collaterals={collaterals} />
  </div>
);
